import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Base } from "../../framework/base";
import {
    IconButton,
    Box,
    Typography,
    Popover,
    Tooltip
} from "@mui/material";
import { AccessTime as AccessTimeIcon, Close as CloseIcon, Check as CheckIcon } from "@mui/icons-material";
import { LocalizationProvider, DesktopTimePicker, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { tooltipProps } from "./utils/OverlapTooltip";

interface TimePickerInlineEditProps {
    label: string;
    start: string;
    end: string;
    handleSave: (start: string, end: string) => Promise<void>;
    hideClockIcon?: boolean;
}

export const TimePickerInlineEdit = ({
    label,
    start,
    end,
    handleSave: onSave,
    hideClockIcon = false,
}: TimePickerInlineEditProps) => {
    const { t } = useTranslation();
    const [startTime, setStartTime] = useState<Dayjs>(dayjs(start));
    const [endTime, setEndTime] = useState<Dayjs>(dayjs(end));
    const [isEditing, setIsEditing] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [hasError, setHasError] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setStartTime(dayjs(start));
        setEndTime(dayjs(end));
    }, [start, end]);

    const validateTimes = (startVal: Dayjs, endVal: Dayjs): boolean => {
        const isValid = endVal.isAfter(startVal);
        setHasError(!isValid);
        return isValid;
    };

    const handleSave = async() => {
        if (!startTime || !endTime) return;
        if (!validateTimes(startTime, endTime)) return;

        setLoading(true);
        try {
            await onSave(
                Base.dayjsToJsonDateTime(startTime),
                Base.dayjsToJsonDateTime(endTime)
            );
            handleClose();
        } finally {
            setLoading(false);
        }
    };

    const handleStartTimeChange = (newTime: Dayjs | null) => {
        if (!newTime) return;
        setStartTime(newTime);
        if (endTime) validateTimes(newTime, endTime);
    };

    const handleEndTimeChange = (newTime: Dayjs | null) => {
        if (!newTime) return;
        setEndTime(newTime);
        if (startTime) validateTimes(startTime, newTime);
    };

    const handleStartDateChange = (newDate: Dayjs | null) => {
        if (!newDate) return;
        const newStart = newDate.hour(startTime.hour()).minute(startTime.minute());
        setStartTime(newStart);
        if (endTime) validateTimes(newStart, endTime);
    };

    const handleEndDateChange = (newDate: Dayjs | null) => {
        if (!newDate) return;
        const newEnd = newDate.hour(endTime.hour()).minute(endTime.minute());
        setEndTime(newEnd);
        if (startTime) validateTimes(startTime, newEnd);
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            void handleSave();
        } else if (event.key === "Escape") {
            handleCancel();
        }
    };

    const handleCancel = () => {
        setStartTime(dayjs(start));
        setEndTime(dayjs(end));
        setHasError(false);
        handleClose();
    };

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setIsEditing(true);
    };

    const handleClose = () => {
        setIsEditing(false);
        setAnchorEl(null);
    };

    const title = React.useMemo(() => {
        try {
            const sameDay = start?.substring(0, 10) === end?.substring(0, 10);
            const startStr = Base.dayjsToTimeStr(start);
            const endStr = sameDay
                ? Base.dayjsToTimeStr(end)
                : Base.dayjsToShortDateTimeStr(end);

            const minutes = Base.dayjsDiffInMinutes(start, end);
            const { hours, minutes: mins } = Base.minutesToHoursAndMinutes(Math.max(0, minutes));
            const hUnit = t("common.abrHours");
            const mUnit = t("common.abrMins");
            const duration = mins > 0 ? `${hours} ${hUnit} ${mins} ${mUnit}` : `${hours} ${hUnit}`;

            return t(
                sameDay
                    ? "workShiftPlanning.timeTooltip.singleDay"
                    : "workShiftPlanning.timeTooltip.multiDay",
                { start: startStr, end: endStr, duration }
            );
        } catch {
            return undefined;
        }
    }, [start, end, t]);

    const labelNode = (
        <Typography
            className="hover-effect"
            fontSize="small"
            fontWeight="600"
            onClick={handleClick}
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{ cursor: "pointer" }}
        >
            {!hideClockIcon && <AccessTimeIcon fontSize="small" color="primary" />}
            {label}
        </Typography>
    );

    return (
        <>
            <LoadingIndicator loading={loading} />
            {title ? (
                <Tooltip title={title} {...tooltipProps}>
                    {labelNode}
                </Tooltip>
            ) : (
                labelNode
            )}
            <Popover
                open={isEditing}
                anchorEl={anchorEl}
                onClose={handleCancel}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                }}
            >
                <Box p={2} onKeyDown={handleKeyDown}>
                    <Box display="flex" justifyContent="flex-end" mb={1} gap={1}>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                                void handleSave();
                            }}
                            title={t("common.save")}
                        >
                            <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                            size="small"
                            onClick={handleCancel}
                            title={t("common.cancel")}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fi">
                        <Box display="flex" gap={3} alignItems="flex-start">
                            <Box>
                                <Box mb={1}>
                                    <DesktopTimePicker
                                        label={t("common.startTime")}
                                        value={startTime}
                                        onChange={handleStartTimeChange}
                                        ampm={false}
                                        autoFocus
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                sx: { width: "170px" },
                                            },
                                            openPickerButton: {
                                                tabIndex: -1,
                                            },
                                        }}
                                    />
                                </Box>
                                <DatePicker
                                    views={["year", "month", "day"]}
                                    value={startTime}
                                    onChange={handleStartDateChange}
                                    format="D.M.YYYY"
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            sx: { width: "170px" },
                                        },
                                        openPickerButton: {
                                            tabIndex: -1,
                                        },
                                    }}
                                />
                            </Box>
                            <Typography alignSelf="center" mt={-2}>
                                -
                            </Typography>
                            <Box>
                                <Box mb={1}>
                                    <DesktopTimePicker
                                        label={t("common.endTime")}
                                        value={endTime}
                                        onChange={handleEndTimeChange}
                                        ampm={false}
                                        slotProps={{
                                            textField: {
                                                size: "small",
                                                sx: { width: "170px" },
                                                error: hasError,
                                            },
                                            openPickerButton: {
                                                tabIndex: -1,
                                            },
                                        }}
                                    />
                                </Box>
                                <DatePicker
                                    views={["year", "month", "day"]}
                                    value={endTime}
                                    onChange={handleEndDateChange}
                                    format="D.M.YYYY"
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            sx: { width: "170px" },
                                            error: hasError,
                                        },
                                        openPickerButton: {
                                            tabIndex: -1,
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    </LocalizationProvider>
                </Box>
            </Popover>
        </>
    );
};